import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime, Interval } from 'luxon';
import * as React from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';

import { createMerchantAdCampaign } from 'apis/ad-bookings/api';
import { MerchantAdCampaignResponse } from 'apis/ad-bookings/types';
import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';

import { CampaignsQueryKeys } from '../../campaignsQueryKeys';
import { useCampaignModuleContext } from '../../context';
import { useCampaignsRoutes } from '../../hooks/useCampaignsRoutes';
import { MerchantAdCampaignFormValues } from '../../types';
import { telemetryNewCampaignDataCreateCampaign } from '../../utils/telemetry/telemetry';
import { transformCreateMerchantAdCampaignRequest } from '../utils/transformAdCampaignFormToApi';

export const useCreateMerchantAdCampaign = () => {
  const history = useHistory();
  const campaignsRoutes = useCampaignsRoutes();
  const queryClient = useQueryClient();
  const { merchantId, currency, country } = useCampaignModuleContext();
  const redirectToCampaignDetails = (campaignId: string) =>
    history.push(`${campaignsRoutes.details(campaignId)}?campaignType=Ads`);

  return useMutation(
    (values: MerchantAdCampaignFormValues) => {
      const payload = transformCreateMerchantAdCampaignRequest(values, merchantId, currency, country);

      return createMerchantAdCampaign(payload);
    },
    {
      onSuccess: async (campaign: MerchantAdCampaignResponse) => {
        toast.success(<FormattedMessage id="campaigns.success-create" />);

        telemetryNewCampaignDataCreateCampaign({
          campaignType: CampaignType.ADS,
          campaignDuration: getCampaignDurationTelemetry(campaign.start_date, campaign.end_date),
        });

        await queryClient.invalidateQueries(CampaignsQueryKeys.ADS.MERCHANT.GET_ALL_CAMPAIGNS(merchantId));
        redirectToCampaignDetails(campaign.id);
      },
      onError: () => {
        toast.error(<FormattedMessage id="campaigns.error-create" />);
      },
    },
  );
};

const getCampaignDurationTelemetry = (startDate: string, endDate: string | undefined): number => {
  // Typical default duration used for telemetry when no duration provided
  if (!endDate) {
    return -9999;
  }

  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);

  return Interval.fromDateTimes(start, end).length('days');
};
